import { CONVEYANCE_TYPES } from '@koala/sdk';
import { format, utcToZonedTime } from 'date-fns-tz';
import { DATE_FORMAT } from '@/constants/dates';
import { type IAdvancedOrderDays } from '@/types/fulfillment';
import { addTime, formatDate, isValidDate } from '@/utils/dates';

// TODO - test
export const deriveDeliveryDates = (
  locationOrderAhead: boolean | undefined,
  locationAdvanceOrderDays: number,
) => {
  const dates: IAdvancedOrderDays[] = [];
  let daysAhead = 1;

  // Days to order ahead, assuming the location supports advanced ordering
  if (locationOrderAhead) {
    daysAhead = daysAhead + locationAdvanceOrderDays;
  }

  for (let i = 0; i < daysAhead; i++) {
    const day = addTime(new Date(), i, 'days');

    dates.push({
      value: formatDate(day, DATE_FORMAT.YEAR_MONTH_DAY_DASHED),
      label: formatDate(day, DATE_FORMAT.LABEL),
    });
  }

  return dates;
};

// TODO - test
export const determineHourType = (type: CONVEYANCE_TYPES) =>
  type === CONVEYANCE_TYPES.DELIVERY ? 'delivery_hours' : 'operating_hours';

/**
 * Get the appropriate time value based on conveyance type
 */
export const getTimeWanted = (
  isDelivery: boolean,
  fulfillment: { address?: { time_wanted?: string | null }; time_wanted?: string | null },
) => {
  const time = isDelivery ? fulfillment.address?.time_wanted : fulfillment.time_wanted;
  return time || undefined; // Convert null to undefined
};

/**
 * Format time for display (e.g., "12:15 PM")
 */
export const formatTimeForDisplay = (timeWanted: string | undefined, storeTimezone: string) => {
  if (!timeWanted || !isValidDate(new Date(timeWanted))) {
    return '';
  }

  return format(utcToZonedTime(timeWanted, storeTimezone), 'h:mm a');
};

/**
 * Check if the time is today
 */
export const isTimeToday = (timeWanted: string | undefined, storeTimezone: string) => {
  if (!timeWanted || !isValidDate(new Date(timeWanted))) {
    return true;
  }

  const today = new Date();
  return (
    format(utcToZonedTime(timeWanted, storeTimezone), 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')
  );
};

/**
 * Format date for display when it's not today (e.g., "Mon, Dec 25")
 */
export const formatDateForDisplay = (timeWanted: string | undefined, storeTimezone: string) => {
  if (!timeWanted || !isValidDate(new Date(timeWanted))) {
    return '';
  }

  return format(utcToZonedTime(timeWanted, storeTimezone), 'EEE, MMM d');
};

/**
 * Get the complete time display string (e.g., "Today by 12:15 PM" or "Mon, Dec 25 by 12:15 PM")
 */
export const getTimeDisplay = (timeWanted: string | undefined, storeTimezone: string) => {
  const formattedTime = formatTimeForDisplay(timeWanted, storeTimezone);
  if (!formattedTime) return '';

  const isToday = isTimeToday(timeWanted, storeTimezone);

  if (isToday) {
    return `Today by ${formattedTime}`;
  }

  const formattedDate = formatDateForDisplay(timeWanted, storeTimezone);
  return `${formattedDate} by ${formattedTime}`;
};
